import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Accessibility, Users, Info, AlertTriangle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { FleetMap } from "@/components/routing/FleetMap";
import { useMapsConfig, fmtDuration, fmtMiles } from "@/lib/maps";

const today = () => new Date().toISOString().slice(0, 10);

const EtaInfo = ({ eta }) => {
  if (!eta) return null;
  if (!eta.available) return <p className="text-xs text-muted-foreground" data-testid="eta-unavailable">{eta.reason}</p>;
  return (
    <div className="text-xs space-y-0.5" data-testid="eta-info">
      <p>Trip drive time: <b>{eta.leg ? `${fmtDuration(eta.leg.duration_s)} · ${fmtMiles(eta.leg.distance_m)}` : eta.leg_reason}</b></p>
      <p>{eta.to_target ? <>Driver ETA to {eta.to_target.target}: <b>{new Date(eta.to_target.eta_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</b> ({fmtDuration(eta.to_target.duration_s)})</> : <span className="text-muted-foreground">ETA: {eta.eta_reason}</span>}</p>
    </div>
  );
};

const TripRow = ({ t, board, canManage, onAssigned, routing, eta, onEta }) => {
  const [driver, setDriver] = useState(t.driver_id || "");
  const [vehicle, setVehicle] = useState(t.vehicle_id || "");
  const [busy, setBusy] = useState(false);
  const locked = ["IN_PROGRESS", "COMPLETED"].includes(t.status);
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dispatch/trips/${t.id}/assign`, { driver_id: driver, vehicle_id: vehicle });
      data.warnings.length ? data.warnings.forEach((w) => toast.warning(w)) : toast.success("Trip dispatched");
      onAssigned();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const sel = "h-9 rounded-md border border-input bg-transparent px-2 text-sm w-full";
  return (
    <div className="p-4 grid lg:grid-cols-[90px_1fr_200px_220px_auto] gap-3 items-center" data-testid="dispatch-trip-row">
      <div className="font-heading font-bold">{(t.scheduled_at || "").slice(11, 16) || "—"}</div>
      <div className="min-w-0">
        <p className="font-semibold truncate flex items-center gap-1.5">{t.client_name || t.title}{t.wheelchair_required && <Accessibility className="w-4 h-4 text-brand" />}</p>
        <p className="text-xs text-muted-foreground truncate">{t.pickup || "—"} → {t.dropoff || "—"}</p>
        <p className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5"><Users className="w-3 h-3" />{t.passengers || 1} · {t.service_tag || t.title} <StatusBadge value={t.dispatch_status || (t.driver_id ? "ASSIGNED" : "UNASSIGNED")} /> <StatusBadge value={t.status} /></p>
      </div>
      <select className={sel} value={driver} disabled={!canManage || locked} onChange={(e) => setDriver(e.target.value)} data-testid="dispatch-driver-select">
        <option value="">— Driver —</option>
        {board.drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.on_duty ? (d.duty_status === "ON_BREAK" ? " · on break" : " · on duty") : ""}</option>)}
      </select>
      <select className={sel} value={vehicle} disabled={!canManage || locked} onChange={(e) => setVehicle(e.target.value)} data-testid="dispatch-vehicle-select">
        <option value="">— Vehicle —</option>
        {board.vehicles.map((v) => <option key={v.id} value={v.id}>{v.code} · cap {v.capacity ?? "?"}{v.wheelchair_accessible ? " · WC" : ""}{v.status && v.status !== "ACTIVE" ? ` · ${v.status}` : ""}</option>)}
      </select>
      {canManage && !locked && <Button size="sm" onClick={assign} disabled={busy} data-testid="dispatch-assign-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}</Button>}
      {routing && (
        <div className="lg:col-span-5 flex flex-wrap items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => onEta(t.id)} data-testid="dispatch-eta-btn"><Navigation className="w-3.5 h-3.5 mr-1" /> Drive time & ETA</Button>
          <EtaInfo eta={eta} />
        </div>
      )}
    </div>
  );
};

export default function Dispatch() {
  const { hasPerm } = useAuth();
  const [day, setDay] = useState(today());
  const [board, setBoard] = useState(null);
  const [live, setLive] = useState([]);
  const [etas, setEtas] = useState({});
  const [selected, setSelected] = useState(null);
  const maps = useMapsConfig();
  const routing = !!maps?.routing_enabled;
  const load = useCallback(() => api.get("/dispatch/board", { params: { date: day } }).then((r) => setBoard(r.data)).catch((e) => toast.error(apiError(e))), [day]);
  useEffect(() => { setBoard(null); load(); }, [load]);
  useEffect(() => { if (hasPerm("tracking:view")) api.get("/tracking/live").then((r) => setLive(r.data)).catch(() => {}); }, [board]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetchEta = async (id) => {
    try { const { data } = await api.get(`/dispatch/trips/${id}/eta`); setEtas((e) => ({ ...e, [id]: data })); setSelected(id); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sel = etas[selected];
  const markers = [
    ...live.filter((s) => s.last_location).map((s) => ({ id: s.id, lat: s.last_location.lat, lng: s.last_location.lng, label: s.driver_name?.[0], title: `${s.driver_name} (driver)`, color: s.stale ? "#9ca3af" : "#141414" })),
    ...(sel?.pickup ? [{ id: "pickup", ...sel.pickup, label: "P", title: "Pickup", color: "#C8202F" }] : []),
    ...(sel?.dropoff ? [{ id: "dropoff", ...sel.dropoff, label: "D", title: "Drop-off", color: "#C8A24A" }] : []),
  ];

  const trips = board?.trips || [];
  const unassigned = trips.filter((t) => !t.driver_id || !t.vehicle_id).length;
  const kpis = [["Trips", trips.length], ["Unassigned", unassigned], ["Drivers on duty", board?.drivers.filter((d) => d.on_duty).length ?? 0], ["Vehicles", board?.vehicles.length ?? 0]];

  return (
    <div data-testid="dispatch-page">
      <PageHeader title="Dispatch Board" subtitle="Assign drivers and vehicles to the day's trips."
        actions={<Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" data-testid="dispatch-date" />} />
      {routing ? (
        <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm mb-5" data-testid="routing-enabled-banner">
          <Navigation className="w-4 h-4 mt-0.5 text-success shrink-0" />
          <span><b>GOOGLE ROUTING ON</b> — drive times and arrival estimates come from Google using real addresses and the driver's latest shared location. Assignment is still done by you.</span>
        </div>
      ) : (
      <div className="flex items-start gap-2 rounded-md border border-ochre/40 bg-ochre/10 p-3 text-sm mb-5" data-testid="manual-dispatch-banner">
        <Info className="w-4 h-4 mt-0.5 text-ochre shrink-0" />
        <span><b>MANUAL DISPATCH</b> — no routing provider is configured, so no routes, ETAs or drive times are calculated. Capacity, wheelchair and vehicle-status rules are enforced; time conflicts are flagged.</span>
      </div>
      )}
      <div className="mb-5"><FleetMap markers={markers} polyline={sel?.to_target?.polyline || sel?.leg?.polyline} height={360} testid="dispatch-map" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {kpis.map(([l, v]) => <div key={l} className="border border-border rounded-lg bg-card p-4" data-testid={`dispatch-kpi-${l.toLowerCase().replace(/ /g, "-")}`}><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="font-heading text-2xl font-bold mt-1">{v}</p></div>)}
      </div>
      {!board ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /> : (
        <div className="border border-border rounded-lg bg-card divide-y divide-border">
          {trips.length === 0 && <p className="p-6 text-sm text-muted-foreground flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> No trips on this date. Generate them from the Master Schedule.</p>}
          {trips.map((t) => <TripRow key={`${t.id}-${t.driver_id}-${t.vehicle_id}`} t={t} board={board} canManage={hasPerm("dispatch:manage")} onAssigned={load} routing={routing} eta={etas[t.id]} onEta={fetchEta} />)}
        </div>
      )}
    </div>
  );
}
